/* ZIVOL P4: the Nightcap second set. Adds ONE more unit of the variant through the same path the product form uses (POST /cart/add with the drawer's
   section ids, then a CartLinesUpdateEvent whose promise carries the new section HTML). cart-items-component (assets/component-cart-items.js,
   #handleCartUpdate, listening for StandardEvents.cartLinesUpdate) then morphs the drawer, and the header count follows the same event. Two callers:
   the offer row's "Add" button ([data-zv-offer-add], printed by snippets/cart-drawer.liquid) and the line stepper's "+" on a Nightcap line
   (assets/component-cart-quantity-selector.js dispatches `zv:nc-add`). Shopify splits the offer into a second cart line, which is why this is an add and
   not a quantity change on the line. The cap (two sets in all lines) is checked against /cart.json before adding. Loaded as a module from scripts.liquid
   (scripts in morphed markup never run). */
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const CAP = 2;
let busy = false;

async function addOne(variantId, source) {
  if (busy || !variantId) return;
  busy = true;
  if (source instanceof HTMLElement) source.setAttribute('aria-busy', 'true');
  const deferred = CartLinesUpdateEvent.createPromise();
  let dispatched = false;
  try {
    const cartRes = await fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    const before = await cartRes.json();
    const have = before.items.filter((i) => String(i.variant_id) === String(variantId)).reduce((n, i) => n + i.quantity, 0);
    if (have >= CAP) return;

    const ids = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    const fd = new FormData();
    fd.append('id', String(variantId));
    fd.append('quantity', '1');
    fd.append('sections', ids.join(','));
    fd.append('sections_url', window.location.pathname);

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );
    dispatched = true;

    const cfg = fetchConfig('javascript', { body: fd });
    const res = await fetch(Theme.routes.cart_add_url, { ...cfg, headers: { ...cfg.headers, Accept: 'text/html' } });
    const json = await res.json();
    if (json.status) throw new Error(json.description || json.message || 'The bag could not be updated');

    const after = await (await fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })).json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(after),
      detail: { sections: json.sections, items: after.items, itemCount: after.item_count, source: 'zv-nc-offer', didError: false },
    });
  } catch (err) {
    if (dispatched) deferred.reject(err);
    document.dispatchEvent(new CartErrorEvent({ error: err.message || 'The bag could not be updated', code: 'SERVICE_UNAVAILABLE' }));
  } finally {
    busy = false;
    if (source instanceof HTMLElement && source.isConnected) source.removeAttribute('aria-busy');
  }
}

document.addEventListener('click', (e) => {
  const btn = e.target instanceof Element ? e.target.closest('[data-zv-offer-add]') : null;
  if (!btn) return;
  e.preventDefault();
  addOne(btn.dataset.variantId, btn);
});
document.addEventListener('zv:nc-add', (e) => addOne(e.detail?.variantId, e.detail?.source));
